import { z } from 'zod';

/**
 * QUANTITIES, AND WHY THEY ARE INTEGERS.
 *
 * Fabric is fractional — 3.75 metres, 0.6 metres of lining — and a movement ledger adds
 * thousands of those together over a shop's lifetime. In binary floating point, 0.1 + 0.2 is
 * not 0.3, and a ledger that accumulates that error tells a shop it has -0.0000000004 metres of
 * something. The whole point of this module is that "where did four metres go?" is answerable,
 * and it stops being answerable the moment the arithmetic drifts.
 *
 * So every quantity in the inventory module is an INTEGER COUNT OF MILLI-UNITS: thousandths of
 * a metre, or thousandths of a piece. It is the same decision the money code makes with paise,
 * for the same reason, and it means the current quantity of an item is `$sum` over integers —
 * exact by construction, however long the ledger gets.
 *
 * A thousandth of a metre is a millimetre, which is finer than any shop measures, and a
 * thousandth of a piece exists only so both units share one representation. Fractions of a
 * piece are refused at the edge rather than silently rounded.
 */

/** One metre, or one piece. */
export const MILLI_UNITS = 1_000;

/** An integer count of thousandths. Never a float, anywhere. */
export type MilliUnits = number;

/** The base check, before a range is put on it. `.refine` returns an effect, so ranges go first. */
const integerMilliUnits = z
  .number()
  .int('Quantities are counted in thousandths and must be whole')
  .max(Number.MAX_SAFE_INTEGER, 'Quantity is out of range');

export const milliUnitsSchema = integerMilliUnits;
export const nonNegativeMilliUnitsSchema = integerMilliUnits.min(0, 'Quantity cannot be negative');
export const positiveMilliUnitsSchema = integerMilliUnits.min(1, 'Enter a quantity');

/**
 * How a shop counts a thing.
 *
 * `metre` and `yard` are the SAME canonical unit — metres — with yards converted at the edge.
 * A shop that buys in yards and cuts in metres must never have two numbers for one roll, so the
 * ledger holds one and the screen shows whichever the shop asked for.
 */
export const STOCK_UNITS = ['metre', 'yard', 'piece'] as const;
export type StockUnit = (typeof STOCK_UNITS)[number];
export const stockUnitSchema = z.enum(STOCK_UNITS);

/** What a unit is STORED as. Two of the three collapse. */
export const CANONICAL_UNITS = ['metre', 'piece'] as const;
export type CanonicalUnit = (typeof CANONICAL_UNITS)[number];
export const canonicalUnitSchema = z.enum(CANONICAL_UNITS);

export function canonicalUnitOf(unit: StockUnit): CanonicalUnit {
  return unit === 'piece' ? 'piece' : 'metre';
}

/**
 * The international yard: exactly 0.9144 metres, by definition since 1959.
 *
 * Expressed as a ratio of integers rather than as 0.9144, so the conversion is exact integer
 * arithmetic. A yard is 9144 ten-thousandths of a metre, so one milli-yard is 9144
 * ten-millionths of a milli-metre — which divides exactly.
 */
const YARD_NUMERATOR = 9_144;
const YARD_DENOMINATOR = 10_000;

/**
 * Convert what somebody typed into what is stored.
 *
 * Rounds ONCE, at this edge, and never again. A shop that enters 5 yards stores 4572 milli-
 * metres exactly; one that enters 3.3 yards stores 3018 (3017.52 rounded), and every subsequent
 * addition of that number is exact.
 */
export function toCanonical(quantity: MilliUnits, unit: StockUnit): MilliUnits {
  if (unit !== 'yard') return quantity;
  return Math.round((quantity * YARD_NUMERATOR) / YARD_DENOMINATOR);
}

/** Convert what is stored into what the shop asked to see. Display only. */
export function fromCanonical(quantity: MilliUnits, unit: StockUnit): MilliUnits {
  if (unit !== 'yard') return quantity;
  return Math.round((quantity * YARD_DENOMINATOR) / YARD_NUMERATOR);
}

/**
 * Parse a typed quantity into milli-units.
 *
 * Deliberately string-first: `"3.75"` from a text input is parsed by splitting on the decimal
 * point rather than by `parseFloat` and multiplying, because `3.75 * 1000` is 3749.9999999999995
 * on some values and the whole point of this file is that it never is here.
 *
 * Returns `null` for anything that is not a quantity, so a caller decides what to say about it.
 */
export function parseQuantity(input: string | number): MilliUnits | null {
  const text = String(input).trim();
  if (text.length === 0) return null;
  if (!/^-?\d*(\.\d*)?$/.test(text) || text === '.' || text === '-') return null;

  const negative = text.startsWith('-');
  const [whole = '0', fraction = ''] = text.replace('-', '').split('.');
  // More than three decimals is finer than a millimetre; the extra digits are dropped rather
  // than rounded, because a shop typing 1.0005 metres means one metre.
  const milli = Number(whole) * MILLI_UNITS + Number((fraction + '000').slice(0, 3));
  if (!Number.isSafeInteger(milli)) return null;
  return negative ? -milli : milli;
}

/** `3.75`, with trailing zeros trimmed — what a shop reads on a screen. */
export function formatQuantity(quantity: MilliUnits, unit: StockUnit = 'metre'): string {
  const shown = fromCanonical(quantity, unit);
  const negative = shown < 0;
  const absolute = Math.abs(shown);
  const whole = Math.floor(absolute / MILLI_UNITS);
  const fraction = String(absolute % MILLI_UNITS)
    .padStart(3, '0')
    .replace(/0+$/, '');
  return `${negative ? '-' : ''}${String(whole)}${fraction ? `.${fraction}` : ''}`;
}

/** The short label a quantity is shown with: `3.75 m`, `12 pcs`. */
export const UNIT_SUFFIX: Readonly<Record<StockUnit, string>> = Object.freeze({
  metre: 'm',
  yard: 'yd',
  piece: 'pcs',
});

export function formatWithUnit(quantity: MilliUnits, unit: StockUnit): string {
  return `${formatQuantity(quantity, unit)} ${UNIT_SUFFIX[unit]}`;
}

/**
 * The value of a quantity at a rate.
 *
 * The rate is paise PER WHOLE UNIT, so the multiplication is `quantity * rate / 1000` and it
 * rounds once. Doing it the other way round — rate per milli-unit — would push the rounding
 * into the rate, where a ₹450/metre fabric becomes ₹0.45/mm and every valuation is out by a
 * few paise for no reason anybody could explain.
 */
export function valueOf(quantity: MilliUnits, ratePaisePerUnit: number): number {
  return Math.round((quantity * ratePaisePerUnit) / MILLI_UNITS);
}

/** True when a quantity in `piece` is a whole number of pieces. Half a button is not a thing. */
export function isWholePieces(quantity: MilliUnits): boolean {
  return quantity % MILLI_UNITS === 0;
}
